import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Edit3, Trash2, CalendarDays, Clock, MapPin, Users, Save, X, Filter, Search, ChevronLeft, ChevronRight } from 'lucide-react';

interface Holiday {
  id: string;
  title: string;
  description: string;
  date: string;
  endDate?: string;
  type: 'public' | 'school' | 'academic' | 'exam' | 'event';
  isRecurring: boolean;
  recurringType?: 'yearly' | 'monthly' | 'weekly';
  location?: string;
  affectedGroups: string[];
  createdAt: string;
  createdBy: string;
}

const HolidayCalendar: React.FC = () => {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [currentDate, setCurrentDate] = useState(new Date());

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    endDate: '',
    type: 'school' as Holiday['type'],
    isRecurring: false,
    recurringType: 'yearly' as Holiday['recurringType'],
    location: '',
    affectedGroups: [] as string[]
  });

  const holidayTypes = [
    { value: 'public', label: 'Public Holiday', color: 'bg-[#341a17] text-[#f38b83]' },
    { value: 'school', label: 'School Holiday', color: 'bg-[#16212b] text-[#60a5fa]' },
    { value: 'academic', label: 'Academic Event', color: 'bg-[#173322] text-[#4ade80]' },
    { value: 'exam', label: 'Examination', color: 'bg-[#332711] text-[#f0b64c]' },
    { value: 'event', label: 'School Event', color: 'bg-[#241f36] text-[#b794f6]' }
  ];

  const affectedGroupOptions = [
    'All Students',
    'Primary Students',
    'Secondary Students',
    'Teachers',
    'Staff',
    'Parents'
  ];

  useEffect(() => {
    loadHolidays();
  }, []);

  const loadHolidays = async () => {
    try {
      setLoading(true);
      // Mock data - would be loaded from API
      const mockHolidays: Holiday[] = [
        {
          id: '1',
          title: 'Diwali',
          description: 'Festival of Lights - School will remain closed',
          date: '2024-11-01',
          type: 'public',
          isRecurring: true,
          recurringType: 'yearly',
          affectedGroups: ['All Students', 'Teachers', 'Staff'],
          createdAt: '2024-01-15T10:00:00Z',
          createdBy: 'admin@school.com'
        },
        {
          id: '2',
          title: 'Annual Sports Day',
          description: 'School annual sports competition and events',
          date: '2024-12-15',
          endDate: '2024-12-16',
          type: 'event',
          isRecurring: true,
          recurringType: 'yearly',
          location: 'School Sports Ground',
          affectedGroups: ['All Students', 'Teachers', 'Parents'],
          createdAt: '2024-01-10T09:00:00Z',
          createdBy: 'admin@school.com'
        },
        {
          id: '3',
          title: 'Winter Break',
          description: 'Winter vacation for all students',
          date: '2024-12-25',
          endDate: '2025-01-05',
          type: 'school',
          isRecurring: true,
          recurringType: 'yearly',
          affectedGroups: ['All Students'],
          createdAt: '2024-01-05T08:00:00Z',
          createdBy: 'admin@school.com'
        },
        {
          id: '4',
          title: 'Mid-term Examinations',
          description: 'Mid-term examination for all classes',
          date: '2024-11-20',
          endDate: '2024-11-30',
          type: 'exam',
          isRecurring: false,
          affectedGroups: ['All Students', 'Teachers'],
          createdAt: '2024-01-20T11:00:00Z',
          createdBy: 'admin@school.com'
        },
        {
          id: '5',
          title: 'Teacher Training Workshop',
          description: 'Professional development workshop for teaching staff',
          date: '2024-11-10',
          type: 'academic',
          isRecurring: false,
          location: 'Conference Hall',
          affectedGroups: ['Teachers'],
          createdAt: '2024-01-25T07:00:00Z',
          createdBy: 'admin@school.com'
        }
      ];
      setHolidays(mockHolidays);
    } catch (error) {
      console.error('Error loading holidays:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHoliday = async () => {
    try {
      const newHoliday: Holiday = {
        id: Date.now().toString(),
        ...formData,
        createdAt: new Date().toISOString(),
        createdBy: 'admin@school.com'
      };

      setHolidays([newHoliday, ...holidays]);
      setShowCreateForm(false);
      resetForm();
    } catch (error) {
      console.error('Error creating holiday:', error);
    }
  };

  const handleUpdateHoliday = async () => {
    if (!editingHoliday) return;

    try {
      const updatedHoliday = {
        ...editingHoliday,
        ...formData
      };

      setHolidays(holidays.map(h => h.id === editingHoliday.id ? updatedHoliday : h));
      setEditingHoliday(null);
      resetForm();
    } catch (error) {
      console.error('Error updating holiday:', error);
    }
  };

  const handleDeleteHoliday = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;

    try {
      setHolidays(holidays.filter(h => h.id !== id));
    } catch (error) {
      console.error('Error deleting holiday:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      date: '',
      endDate: '',
      type: 'school',
      isRecurring: false,
      recurringType: 'yearly',
      location: '',
      affectedGroups: []
    });
  };

  const startEdit = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      title: holiday.title,
      description: holiday.description,
      date: holiday.date,
      endDate: holiday.endDate || '',
      type: holiday.type,
      isRecurring: holiday.isRecurring,
      recurringType: holiday.recurringType || 'yearly',
      location: holiday.location || '',
      affectedGroups: holiday.affectedGroups
    });
    setShowCreateForm(true);
  };

  const filteredHolidays = holidays.filter(holiday => {
    const matchesSearch = holiday.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         holiday.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || holiday.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTypeColor = (type: string) => {
    return holidayTypes.find(t => t.value === type)?.color || 'bg-[#28221a] text-[#b9ad9b]';
  };

  const getTypeLabel = (type: string) => {
    return holidayTypes.find(t => t.value === type)?.label || type;
  };

  // Calendar utility functions
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getMonthName = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const getHolidaysForDate = (day: number) => {
    if (!day) return [];

    const dateString = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    return filteredHolidays.filter(holiday => {
      const holidayStart = new Date(holiday.date);
      const holidayEnd = holiday.endDate ? new Date(holiday.endDate) : holidayStart;
      const currentDateObj = new Date(dateString);

      return currentDateObj >= holidayStart && currentDateObj <= holidayEnd;
    });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#e2916d]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-[#e2916d] rounded-lg flex items-center justify-center">
            <Calendar className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#f3ede2]">Holiday Calendar</h1>
            <p className="text-[#b9ad9b] mt-1">Manage school holidays, events, and academic calendar</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex bg-[#211c15] rounded-lg p-1">
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'calendar'
                  ? 'bg-[#211c15] text-[#e2916d] shadow-sm'
                  : 'text-[#b9ad9b] hover:text-[#f3ede2]'
              }`}
            >
              Calendar
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-[#211c15] text-[#e2916d] shadow-sm'
                  : 'text-[#b9ad9b] hover:text-[#f3ede2]'
              }`}
            >
              List
            </button>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center space-x-2 bg-[#e2916d] text-white px-4 py-2 rounded-lg hover:bg-[#f0a67d] transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Holiday</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="w-5 h-5 text-[#8a7f6e] absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search holidays..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full pl-10 pr-4 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-[#8a7f6e]" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
            >
              <option value="all">All Types</option>
              {holidayTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' ? (
        <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
          {/* Calendar Header */}
          <div className="p-6 border-b border-[#3a3122]">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#f3ede2]">{getMonthName(currentDate)}</h2>
              <div className="flex items-center space-x-4">
                <button
                  onClick={goToToday}
                  className="px-3 py-1 text-sm text-[#e2916d] hover:bg-[#332217] rounded-lg transition-colors"
                >
                  Today
                </button>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => navigateMonth('prev')}
                    className="p-2 text-[#8a7f6e] hover:text-[#f3ede2] hover:bg-[#28221a] rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigateMonth('next')}
                    className="p-2 text-[#8a7f6e] hover:text-[#f3ede2] hover:bg-[#28221a] rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="p-6">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-3 text-center text-sm font-medium text-[#b9ad9b]">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {generateCalendarDays().map((day, index) => {
                const dayHolidays = day ? getHolidaysForDate(day) : [];
                const isToday = day &&
                  currentDate.getFullYear() === new Date().getFullYear() &&
                  currentDate.getMonth() === new Date().getMonth() &&
                  day === new Date().getDate();

                return (
                  <div
                    key={index}
                    className={`min-h-[100px] p-2 border border-[#3a3122] rounded-lg ${
                      !day ? 'bg-[#17140f]' : 'bg-[#211c15] hover:bg-[#28221a]'
                    } transition-colors`}
                  >
                    {day && (
                      <>
                        <div className={`text-sm font-medium mb-1 ${
                          isToday ? 'text-[#e2916d]' : 'text-[#f3ede2]'
                        }`}>
                          {day}
                          {isToday && (
                            <div className="w-1.5 h-1.5 bg-[#e2916d] rounded-full inline-block ml-1"></div>
                          )}
                        </div>
                        <div className="space-y-1">
                          {dayHolidays.slice(0, 2).map(holiday => (
                            <div
                              key={holiday.id}
                              className={`text-xs p-1 rounded text-white cursor-pointer ${
                                holiday.type === 'public' ? 'bg-red-500' :
                                holiday.type === 'school' ? 'bg-blue-500' :
                                holiday.type === 'academic' ? 'bg-green-500' :
                                holiday.type === 'exam' ? 'bg-orange-500' :
                                'bg-purple-500'
                              }`}
                              title={`${holiday.title} - ${holiday.description}`}
                              onClick={() => startEdit(holiday)}
                            >
                              {holiday.title.length > 15 ? `${holiday.title.substring(0, 15)}...` : holiday.title}
                            </div>
                          ))}
                          {dayHolidays.length > 2 && (
                            <div className="text-xs text-[#8a7f6e] font-medium">
                              +{dayHolidays.length - 2} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Calendar Legend */}
          <div className="p-6 border-t border-[#3a3122]">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-[#f3ede2]">Legend:</span>
              {holidayTypes.map(type => (
                <div key={type.value} className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded ${
                    type.value === 'public' ? 'bg-red-500' :
                    type.value === 'school' ? 'bg-blue-500' :
                    type.value === 'academic' ? 'bg-green-500' :
                    type.value === 'exam' ? 'bg-orange-500' :
                    'bg-purple-500'
                  }`}></div>
                  <span className="text-sm text-[#8a7f6e]">{type.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* List View */
        <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
          <div className="p-6 border-b border-[#3a3122]">
            <h2 className="text-lg font-semibold text-[#f3ede2]">
              All Holidays & Events
              <span className="ml-2 text-sm font-normal text-[#b9ad9b]">
                ({filteredHolidays.length} {filteredHolidays.length === 1 ? 'item' : 'items'})
              </span>
            </h2>
          </div>

          <div className="divide-y divide-[#3a3122]">
            {filteredHolidays.length === 0 ? (
              <div className="p-8 text-center">
                <CalendarDays className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#f3ede2] mb-2">No holidays found</h3>
                <p className="text-[#8a7f6e]">
                  {searchTerm || filterType !== 'all'
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Get started by adding your first holiday or event.'
                  }
                </p>
              </div>
            ) : (
              filteredHolidays.map((holiday) => (
                <div key={holiday.id} className="p-6 hover:bg-[#28221a] transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-medium text-[#f3ede2]">{holiday.title}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(holiday.type)}`}>
                          {getTypeLabel(holiday.type)}
                        </span>
                        {holiday.isRecurring && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-[#332217] text-[#f0a67d]">
                            Recurring
                          </span>
                        )}
                      </div>

                      <p className="text-[#b9ad9b] mb-3">{holiday.description}</p>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-[#8a7f6e]">
                        <div className="flex items-center space-x-1">
                          <CalendarDays className="w-4 h-4" />
                          <span>
                            {formatDate(holiday.date)}
                            {holiday.endDate && ` - ${formatDate(holiday.endDate)}`}
                          </span>
                        </div>

                        {holiday.location && (
                          <div className="flex items-center space-x-1">
                            <MapPin className="w-4 h-4" />
                            <span>{holiday.location}</span>
                          </div>
                        )}

                        <div className="flex items-center space-x-1">
                          <Users className="w-4 h-4" />
                          <span>{holiday.affectedGroups.join(', ')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => startEdit(holiday)}
                        className="p-2 text-[#8a7f6e] hover:text-[#e2916d] hover:bg-[#332217] rounded-lg transition-colors"
                        title="Edit holiday"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteHoliday(holiday.id)}
                        className="p-2 text-[#8a7f6e] hover:text-[#f38b83] hover:bg-[#341a17] rounded-lg transition-colors"
                        title="Delete holiday"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Holiday Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-always-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#211c15] rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-[#f3ede2]">
                {editingHoliday ? 'Edit Holiday' : 'Add New Holiday'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingHoliday(null);
                  resetForm();
                }}
                className="p-2 text-[#8a7f6e] hover:text-[#f3ede2] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              editingHoliday ? handleUpdateHoliday() : handleCreateHoliday();
            }} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                    placeholder="Holiday or event title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Type *
                  </label>
                  <select
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value as Holiday['type']})}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  >
                    {holidayTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    End Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  placeholder="Brief description of the holiday or event"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                  Location (Optional)
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  placeholder="Event location or venue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                  Affected Groups *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {affectedGroupOptions.map(group => (
                    <label key={group} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.affectedGroups.includes(group)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              affectedGroups: [...formData.affectedGroups, group]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              affectedGroups: formData.affectedGroups.filter(g => g !== group)
                            });
                          }
                        }}
                        className="rounded border-[#3a3122] text-[#e2916d] focus:ring-[#e2916d]"
                      />
                      <span className="text-sm text-[#f3ede2]">{group}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.isRecurring}
                    onChange={(e) => setFormData({...formData, isRecurring: e.target.checked})}
                    className="rounded border-[#3a3122] text-[#e2916d] focus:ring-[#e2916d]"
                  />
                  <span className="text-sm font-medium text-[#f3ede2]">Recurring Event</span>
                </label>

                {formData.isRecurring && (
                  <select
                    value={formData.recurringType}
                    onChange={(e) => setFormData({...formData, recurringType: e.target.value as Holiday['recurringType']})}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] px-3 py-1 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  >
                    <option value="yearly">Yearly</option>
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                  </select>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingHoliday(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-[#b9ad9b] border border-[#3a3122] rounded-lg hover:bg-[#28221a] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!formData.title || !formData.date || formData.affectedGroups.length === 0}
                  className="flex items-center space-x-2 bg-[#e2916d] text-white px-4 py-2 rounded-lg hover:bg-[#f0a67d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  <span>{editingHoliday ? 'Update Holiday' : 'Create Holiday'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HolidayCalendar;